import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiLogin, apiMe, getMyShops, getMyFactories } from '../services/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Shops/factories the current user has access to
  const [myShops, setMyShops] = useState([]);
  const [myFactories, setMyFactories] = useState([]);

  // Currently active shop (for shop_worker / super_admin creating orders)
  const [selectedShop, setSelectedShopState] = useState(null);
  // Currently active factory (for factory_worker viewing queue)
  const [selectedFactory, setSelectedFactoryState] = useState(null);

  const setSelectedShop = (shop) => {
    setSelectedShopState(shop);
    if (shop) localStorage.setItem('orders_selectedShop', JSON.stringify(shop));
    else localStorage.removeItem('orders_selectedShop');
  };
  const setSelectedFactory = (factory) => {
    setSelectedFactoryState(factory);
    if (factory) localStorage.setItem('orders_selectedFactory', JSON.stringify(factory));
    else localStorage.removeItem('orders_selectedFactory');
  };

  // Loads /mine shops + factories for the current user and restores selection
  const loadContext = async (userObj) => {
    const isShop = userObj.role === 'shop_worker' || userObj.role === 'super_admin';
    const isFactory = userObj.role === 'factory_worker' || userObj.role === 'super_admin';
    const tasks = [];
    if (isShop) tasks.push(getMyShops().then(r => r.data).catch(() => []));
    else tasks.push(Promise.resolve([]));
    if (isFactory) tasks.push(getMyFactories().then(r => r.data).catch(() => []));
    else tasks.push(Promise.resolve([]));
    const [shops, factories] = await Promise.all(tasks);
    setMyShops(shops);
    setMyFactories(factories);

    // Restore selected shop from localStorage if still valid; else pick first
    let storedShop = null;
    try { storedShop = JSON.parse(localStorage.getItem('orders_selectedShop') || 'null'); } catch (e) {}
    const matchedShop = storedShop && shops.find(s => s.id === storedShop.id);
    if (matchedShop) setSelectedShopState(matchedShop);
    else if (shops.length > 0) { setSelectedShopState(shops[0]); localStorage.setItem('orders_selectedShop', JSON.stringify(shops[0])); }
    else { setSelectedShopState(null); localStorage.removeItem('orders_selectedShop'); }

    let storedFactory = null;
    try { storedFactory = JSON.parse(localStorage.getItem('orders_selectedFactory') || 'null'); } catch (e) {}
    const matchedFactory = storedFactory && factories.find(f => f.id === storedFactory.id);
    if (matchedFactory) setSelectedFactoryState(matchedFactory);
    else if (factories.length > 0) { setSelectedFactoryState(factories[0]); localStorage.setItem('orders_selectedFactory', JSON.stringify(factories[0])); }
    else { setSelectedFactoryState(null); localStorage.removeItem('orders_selectedFactory'); }
  };

  useEffect(() => {
    const token = localStorage.getItem('orders_token');
    if (!token) { setLoading(false); return; }
    apiMe()
      .then(async (res) => {
        setUser(res.data);
        await loadContext(res.data);
      })
      .catch(() => {
        localStorage.removeItem('orders_token');
        localStorage.removeItem('orders_user');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    const res = await apiLogin(username, password);
    const { token, user: u } = res.data;
    localStorage.setItem('orders_token', token);
    localStorage.setItem('orders_user', JSON.stringify(u));
    setUser(u);
    await loadContext(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem('orders_token');
    localStorage.removeItem('orders_user');
    localStorage.removeItem('orders_selectedShop');
    localStorage.removeItem('orders_selectedFactory');
    setUser(null);
    setMyShops([]);
    setMyFactories([]);
    setSelectedShopState(null);
    setSelectedFactoryState(null);
  };

  const value = {
    user, loading, login, logout,
    myShops, myFactories,
    selectedShop, setSelectedShop,
    selectedFactory, setSelectedFactory,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
